"use client";

import React, { useState, useEffect } from "react";
import OptionField from "./OptionField";
import { usePathname } from "next/navigation";
import { checkCondition } from "@/helpers/formConditionHelper";

const Wrapper = ({ condition, wrapper, children }) => {
  return condition ? wrapper(children) : children;
};

const SelectField = ({
  customErrorInputStyle = null,
  customErrorLabelStyle = null,
  customErrorMessageStyle = null,
  errorMessage,
  externalDataSource,
  idField,
  isValid = true,
  items,
  label,
  labelClassName,
  inputWrapperClassName,
  labelWrapperClassName,
  name,
  onChangeHandler,
  required,
  selectClassName,
  description,
  explanation,
  inputsData,
}) => {
  const path = usePathname();
  const hasTeacheInquiryForm =
    path.includes("find-a-teacher") ||
    path.includes("study-rcm-simplified-chinese") ||
    path.includes("study-rcm-traditional-chinese") ||
    path.includes("/teachers/");
  const [selectData, setSelectData] = useState([]);
  const isSC = path.includes("/study-rcm-simplified-chinese");
  const isTC = path.includes("/study-rcm-traditional-chinese");

  const getInputStyles = (isValid, customErrorInputStyle) => {
    if (!isValid && customErrorInputStyle) {
      return customErrorInputStyle;
    }
    return {
      border: isValid ? "1px solid #ccc" : "1px solid #960000",
      background: isValid ? "" : "#f9f0f0",
      width: "100%",
    };
  };

  const getLabelStyles = (isValid, customErrorLabelStyle) => {
    if (!isValid && customErrorLabelStyle) {
      return customErrorLabelStyle;
    }
    return {
      color: isValid ? "" : "#960000",
    };
  };

  useEffect(() => {
    const externalData = externalDataSource
      ? fetch(
          `${process.env.NEXT_PUBLIC_STAPI_USE_CLIENT_URL || process.env.NEXT_PUBLIC_STAPI_URL}/api/${
            !!hasTeacheInquiryForm ? "teachers-ntd-" : ""
          }${externalDataSource}?pagination[page]=1&pagination[pageSize]=50&sort[name]=asc`
        )
          .then((res) => res.json())
          .then((extData) => {
            return extData.data;
          })
      : null;

    if (externalDataSource) {
      const printExternalData = async () => {
        const ed = await externalData;
        setSelectData(ed);
      };

      printExternalData();
    }
  }, []);

  return (
    <div className="tps-form-question">
      <Wrapper
        condition={!labelWrapperClassName}
        wrapper={(children) => <div className="tps-form-label">{children}</div>}
      >
        <label
          className={`${labelClassName ? labelClassName : ""} ${
            required ? "tps-field-required tps-to-validate" : "tps-not-required"
          }`}
          htmlFor={idField}
          style={getLabelStyles(isValid, customErrorLabelStyle)}
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        ></label>
        {!!explanation && explanation !== "" && (
          <div className="fas fa-info-circle tps-form-tip has-tip">
            <div
              className="tooltiptext"
              dangerouslySetInnerHTML={{ __html: explanation }}
            ></div>
          </div>
        )}
      </Wrapper>

      <Wrapper
        condition={!inputWrapperClassName}
        wrapper={(children) => <div className="tps-form-input">{children}</div>}
      >
        <select
          className={selectClassName ? selectClassName : "form-select"}
          data-algo="facet"
          data-algo-default-value=""
          data-algo-facet={`${name}`}
          data-algo-facet-populate="true"
          id={idField}
          name={name}
          style={getInputStyles(isValid, customErrorInputStyle)}
          required={required}
          onChange={(event) => onChangeHandler(event)}
          defaultValue=""
          suppressHydrationWarning={true}
        >
          <OptionField
            key={"default-option-field"}
            value={""}
            label={isSC ? "- 请选择 -" : isTC ? "- 請選擇 -" : "- Select -"}
          />
          {externalDataSource
            ? selectData?.map((data) => (
                <OptionField key={data.id} value={data.attributes.name || ""} />
              ))
            : items
                .filter((option) => {
                  if (
                    option.display_condition === null ||
                    option.display_condition === undefined ||
                    (!!option.display_condition &&
                      checkCondition(option.display_condition, inputsData))
                  ) {
                    return true;
                  }
                  return false;
                })
                .map((option, index) => (
                  <OptionField
                    key={index}
                    value={
                      (option instanceof Object ? option.value : option) || ""
                    }
                    label={
                      (option instanceof Object ? option.label : option) || ""
                    }
                  />
                ))}
        </select>
        {!!description && description !== "" && (
          <p
            className="tps-form-input-explaination"
            dangerouslySetInnerHTML={{ __html: description }}
          ></p>
        )}
      </Wrapper>
      {!isValid && (
        <label
          className={`fatv2-body form-control tps-field-required EditingFormErrorLabel`}
          htmlFor={idField || name}
          style={customErrorMessageStyle}
        >
          {errorMessage}
        </label>
      )}
    </div>
  );
};

export default SelectField;
